import { useEffect, useRef, useState } from 'react';
import { CameraOutlined, CheckCircleOutlined, CloseOutlined, PictureOutlined, WarningOutlined } from '@ant-design/icons';

import { Button, Spinner } from '../../../components/ui';
import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { CANH_NGAN_TOI_THIEU, TI_LE_THE, phanLoaiLoiCamera, quaNho, type LyDoCamera } from '../nghiep-vu/camera';

const LOI_CAMERA: Record<LyDoCamera, KhoaDich> = {
  KHONG_BAO_MAT: 'ekyc.chupAnh.loiKhongBaoMat',
  TU_CHOI: 'ekyc.chupAnh.loiTuChoi',
  KHONG_CO_THIET_BI: 'ekyc.chupAnh.loiKhongCoThietBi',
  DANG_BAN: 'ekyc.chupAnh.loiDangBan',
  KHAC: 'ekyc.chupAnh.loiKhac',
};

const MEO: Record<'giay-to' | 'chan-dung', KhoaDich[]> = {
  'giay-to': ['ekyc.chupAnh.meoGiayTo1', 'ekyc.chupAnh.meoGiayTo2', 'ekyc.chupAnh.meoGiayTo3', 'ekyc.chupAnh.meoGiayTo4'],
  'chan-dung': ['ekyc.chupAnh.meoChanDung1', 'ekyc.chupAnh.meoChanDung2', 'ekyc.chupAnh.meoChanDung3'],
};

/** Kích thước thật của một tệp ảnh; `null` khi trình duyệt không giải được (HEIC) — khi đó vẫn gửi. */
async function docKichThuoc(tep: Blob): Promise<{ rong: number; cao: number } | null> {
  try {
    const bm = await createImageBitmap(tep);
    const kt = { rong: bm.width, cao: bm.height };
    bm.close();
    return kt;
  } catch {
    return null;
  }
}

/**
 * Chụp một ảnh: **hai nút riêng**, "Mở camera" và "Chọn ảnh có sẵn" — không phải một `<input capture>`.
 *
 * `capture` chỉ là gợi ý: điện thoại mở camera, máy tính bỏ qua và mở hộp chọn tệp. Một nút, hai hành vi, và người
 * đứng ở quầy với webcam không có cách nào chụp thẳng — đúng lỗi "thiếu phần chụp ảnh" đã gặp ở bước giấy tờ.
 *
 * - **Chụp ở độ phân giải gốc** của camera (canvas bằng `videoWidth × videoHeight`, JPEG 0,92). Thu nhỏ "cho nhẹ"
 *   là tự tạo ra một lần từ chối mà người dùng không hiểu vì sao: máy chủ đòi cạnh ngắn ≥ 480 px.
 * - **Không bao giờ lật ảnh lưu.** Khung xem trước camera trước được lật bằng CSS cho dễ căn, nhưng ảnh lưu giữ đúng
 *   chiều — chữ bị lật thì OCR hỏng, mặt bị lật thì trái phải đổi chỗ.
 * - **Tắt camera khi xong hoặc khi rời màn**, để đèn camera không sáng mãi.
 * - Ảnh vừa chụp hiện lại ngay ở đây từ **bản trên máy**, không tải từ máy chủ: mở ảnh từ máy chủ là một lượt xem dữ
 *   liệu cá nhân có ghi nhật ký, không phải thứ để làm ảnh thu nhỏ.
 */
export function ChupAnh({
  ten,
  loai,
  camera,
  dangGui,
  daNhan,
  onChon,
}: {
  ten: string;
  loai: 'giay-to' | 'chan-dung';
  camera: 'user' | 'environment';
  dangGui: boolean;
  /** Máy chủ đã nhận ảnh: hiện dấu đã nhận cạnh ảnh xem lại. */
  daNhan: { rong: number; cao: number } | null;
  /** Ảnh đã qua kiểm ở trình duyệt, sẵn sàng tải lên. */
  onChon: (tep: File) => void;
}) {
  const { t } = useT();
  const videoRef = useRef<HTMLVideoElement>(null);
  const tepRef = useRef<HTMLInputElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [moCamera, setMoCamera] = useState(false);
  const [dangMo, setDangMo] = useState(false);
  const [loiCamera, setLoiCamera] = useState<LyDoCamera | null>(null);
  const [cameraYeu, setCameraYeu] = useState(false);
  const [loiNho, setLoiNho] = useState<{ rong: number; cao: number } | null>(null);
  const [xemLai, setXemLai] = useState<string | null>(null);

  const tatCamera = () => {
    streamRef.current?.getTracks().forEach((tr) => tr.stop());
    streamRef.current = null;
    setMoCamera(false);
    setCameraYeu(false);
  };

  // Rời màn (huỷ phiên, đổi trang) giữa lúc camera đang mở: tắt luồng, thu hồi ảnh xem lại.
  useEffect(() => () => {
    streamRef.current?.getTracks().forEach((tr) => tr.stop());
  }, []);
  useEffect(() => () => {
    if (xemLai) URL.revokeObjectURL(xemLai);
  }, [xemLai]);

  const guiTep = async (tep: File) => {
    const kt = await docKichThuoc(tep);
    if (quaNho(kt)) {
      setLoiNho(kt);
      return;
    }
    setLoiNho(null);
    setXemLai(URL.createObjectURL(tep));
    onChon(tep);
  };

  const batCamera = async () => {
    setLoiCamera(null);
    setLoiNho(null);
    const md = window.isSecureContext ? navigator.mediaDevices : undefined;
    if (!md?.getUserMedia) {
      setLoiCamera(phanLoaiLoiCamera(null, false));
      return;
    }
    setDangMo(true);
    try {
      const stream = await md.getUserMedia({ video: { facingMode: camera, width: { ideal: 1920 } }, audio: false });
      streamRef.current = stream;
      setMoCamera(true);
      // Gắn luồng sau khi thẻ video đã vẽ ra.
      requestAnimationFrame(() => {
        const v = videoRef.current;
        if (!v) return;
        v.srcObject = stream;
        void v.play();
      });
    } catch (e) {
      setLoiCamera(phanLoaiLoiCamera(e, true));
    } finally {
      setDangMo(false);
    }
  };

  const chup = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const c = document.createElement('canvas');
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext('2d')?.drawImage(v, 0, 0, c.width, c.height);
    c.toBlob((blob) => {
      if (!blob) return;
      tatCamera();
      void guiTep(new File([blob], `${loai}-${Date.now()}.jpg`, { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.92);
  };

  const lat = camera === 'user';

  return (
    <div className="space-y-3">
      {moCamera ? (
        <div className="space-y-2">
          <div className="relative overflow-hidden rounded-lg bg-subtle">
            <video
              ref={videoRef}
              muted
              playsInline
              aria-label={t('ekyc.chupAnh.khungCamera', { ten })}
              className={`block w-full ${lat ? '-scale-x-100' : ''}`}
              onLoadedMetadata={(e) => {
                const v = e.currentTarget;
                setCameraYeu(quaNho({ rong: v.videoWidth, cao: v.videoHeight }));
              }}
            />
            {/* Khung căn: chỉ để nhìn, không cắt ảnh. */}
            <div aria-hidden className="pointer-events-none absolute inset-0 flex items-center justify-center p-4">
              {loai === 'giay-to' ? (
                <div className="w-5/6 rounded-lg border-2 border-dashed border-primary-500" style={{ aspectRatio: TI_LE_THE }} />
              ) : (
                <div className="h-5/6 rounded-full border-2 border-dashed border-primary-500" style={{ aspectRatio: '3 / 4' }} />
              )}
            </div>
          </div>
          {cameraYeu && (
            <p role="note" className="flex items-start gap-1.5 text-xs text-warning-700">
              <WarningOutlined className="mt-0.5 shrink-0" />
              <span className="min-w-0">{t('ekyc.chupAnh.cameraYeu', { min: CANH_NGAN_TOI_THIEU })}</span>
            </p>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Button variant="primary" size="md" icon={<CameraOutlined />} onClick={chup}>
              {t('ekyc.chupAnh.chup')}
            </Button>
            <Button variant="outline" size="md" icon={<CloseOutlined />} onClick={tatCamera}>
              {t('ekyc.chupAnh.dong')}
            </Button>
          </div>
        </div>
      ) : (
        <>
          {xemLai ? (
            <figure className="relative overflow-hidden rounded-lg border border-default bg-subtle">
              <img src={xemLai} alt={t('ekyc.chupAnh.anhVuaChup', { ten })} className="block max-h-72 w-full object-contain" />
              <figcaption className="flex items-center gap-1.5 border-t border-default bg-surface px-3 py-2 text-xs">
                {dangGui ? (
                  <span className="flex items-center gap-1.5 text-content-secondary"><Spinner /> {t('ekyc.dinhDanh.dangTaiAnh')}</span>
                ) : daNhan ? (
                  <span className="flex items-center gap-1.5 text-success-700">
                    <CheckCircleOutlined /> {t('ekyc.dinhDanh.daNhanAnh', { rong: daNhan.rong, cao: daNhan.cao })}
                  </span>
                ) : (
                  <span className="text-content-muted">{t('ekyc.chupAnh.chuaNhan')}</span>
                )}
              </figcaption>
            </figure>
          ) : (
            <div className="rounded-lg border border-default bg-subtle p-4">
              <p className="text-sm font-medium text-content-primary">
                {t(loai === 'giay-to' ? 'ekyc.chupAnh.meoTieuDeGiayTo' : 'ekyc.chupAnh.meoTieuDeChanDung')}
              </p>
              <ul className="mt-1.5 list-disc space-y-1 pl-5 text-xs text-content-secondary">
                {MEO[loai].map((k) => <li key={k}>{t(k)}</li>)}
              </ul>
            </div>
          )}

          <input
            ref={tepRef}
            type="file"
            accept="image/jpeg,image/png"
            className="sr-only"
            tabIndex={-1}
            aria-label={t('ekyc.chupAnh.chonCoSanCua', { ten })}
            onChange={(e) => {
              const tep = e.target.files?.[0];
              // Xoá giá trị để chọn lại đúng tệp vừa chọn vẫn kích hoạt `onChange`.
              e.target.value = '';
              if (tep) void guiTep(tep);
            }}
          />
          <div className="grid gap-2 sm:grid-cols-2">
            <Button
              variant="outline"
              size="md"
              icon={dangMo ? <Spinner /> : <CameraOutlined />}
              disabled={dangGui || dangMo}
              onClick={() => void batCamera()}
            >
              {xemLai ? t('ekyc.chupAnh.chupLaiCamera') : t('ekyc.chupAnh.moCamera')}
            </Button>
            <Button
              variant="outline"
              size="md"
              icon={<PictureOutlined />}
              disabled={dangGui}
              onClick={() => tepRef.current?.click()}
            >
              {t('ekyc.chupAnh.chonCoSan')}
            </Button>
          </div>
        </>
      )}

      {loiCamera && (
        <p role="alert" className="text-xs text-danger-700">{t(LOI_CAMERA[loiCamera])}</p>
      )}
      {loiNho && (
        <p role="alert" className="text-xs text-danger-700">
          {t('ekyc.chupAnh.quaNho', { rong: loiNho.rong, cao: loiNho.cao, min: CANH_NGAN_TOI_THIEU })}
        </p>
      )}
    </div>
  );
}
